/**
 * INSTALLING SCOUT AS AN APPLICATION, FROM THE PAGE'S SIDE.
 *
 * WHY THIS FILE EXISTS AT ALL. The manifest has been installable since Slice
 * 10 — `display: standalone`, a `start_url` that lands on the Main Chat, and
 * the 192/512/maskable icons Chrome requires — and the browser acceptance
 * asserts every one of those facts. What the workspace did NOT have was any
 * way for the Owner to ASK. A manifest is the thing that makes installation
 * POSSIBLE; it is not a control, and the Owner's flow ("authenticate, select
 * Install Scout, open it from the home screen") names a control.
 * `scripts/scout-web-pwa.test.ts` pins the manifest half and cannot speak for
 * a button that did not exist.
 *
 * THE RULE THIS FILE IS WRITTEN TO: SAY WHAT IS TRUE ABOUT THIS BROWSER, AND
 * NOTHING ELSE. Four states, and each is a fact rather than a shade of
 * optimism:
 *
 *   - `installed`  — this window IS the installed application, or the browser
 *                    told us the installation completed. No control is shown:
 *                    offering to install something already installed is the
 *                    duplicate prompt the brief forbids.
 *   - `promptable` — the browser handed us a deferred prompt. The only state
 *                    in which a one-tap install is REAL. Until this arrives,
 *                    Chromium has not decided the app is installable, and a
 *                    button that called `prompt()` with nothing behind it
 *                    would do nothing while looking like it did.
 *   - `manual`     — a real browser that installs applications, but only
 *                    through its own menu (Safari, and Chromium before the
 *                    event fires). The instructions are this browser's ACTUAL
 *                    menu path, not a generic "check your browser settings".
 *   - `unsupported`— a browser or a context where installation does not exist.
 *                    Nothing is offered, because nothing can be done.
 *
 * WHAT IT MUST NEVER DO, and each is a line the brief drew:
 *   - no fake download link, and no APK — there is no file to download;
 *   - no success message the browser did not report (`userChoice` is the
 *     outcome, and "dismissed" is reported as dismissed);
 *   - no prompt raised on load, and no second prompt after the first answer;
 *   - no effect on ordinary use when the Owner declines — the workspace is
 *     unchanged, which is also why nothing here is load-bearing.
 *
 * THE LISTENER IS REGISTERED AT MODULE SCOPE, AND THAT IS DELIBERATE.
 * `beforeinstallprompt` can fire before this application mounts, and an event
 * that is missed is indistinguishable from a browser that does not support
 * installation. Every other deferred-listener in this workspace is started from
 * an effect, which is right for events about the page; this one is about the
 * browser, so it is caught as early as the bundle evaluates.
 */

import { signal } from '@preact/signals';

/** The subset of the non-standard `BeforeInstallPromptEvent` this uses. It is
 *  not in lib.dom, and typing it as `any` would hide exactly the calls that
 *  matter — `prompt()` and the `userChoice` promise. */
export interface InstallPromptEvent extends Event {
	prompt(): Promise<void>;
	readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallCapability =
	/** Already running as the installed application. */
	| { kind: 'installed'; detail: string }
	/** The browser offered a deferred prompt; a one-tap install is real. */
	| { kind: 'promptable' }
	/** Installable, but only through the browser's own menu. */
	| { kind: 'manual'; detail: string }
	/** Installation does not exist here. */
	| { kind: 'unsupported'; detail: string };

/** The deferred event, kept OUT of the signal: it is a live object with a
 *  one-shot method, and a signal holding it would invite a view to read it. */
let deferred: InstallPromptEvent | null = null;

/** True once the browser has said the application was installed. Set by the
 *  `appinstalled` event AND by a `userChoice` of `accepted`, because the two
 *  arrive in either order and neither alone is reliable. */
const installReported = signal<boolean>(false);

/** Bumped whenever the deferred prompt arrives or is consumed, so a rendered
 *  control re-reads `installCapability()` without polling. */
const promptRevision = signal<number>(0);

/** Is this window the installed application? `display-mode: standalone` covers
 *  Android and desktop; `navigator.standalone` is iOS Safari's own older
 *  spelling of the same fact, and it is checked because iOS never fires
 *  `beforeinstallprompt` and would otherwise be reported as merely `manual`
 *  forever after it was installed. */
function runningStandalone(): boolean {
	if (typeof window === 'undefined') return false;
	if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
		return true;
	}
	const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
	return legacy === true;
}

/** Which browser is this, as far as installation instructions go? Reported by
 *  NAME because the menu path differs and a generic sentence helps nobody. */
function instructionBrowser(): 'ios-safari' | 'firefox' | 'other' {
	if (typeof navigator === 'undefined') return 'other';
	const agent = navigator.userAgent;
	// iPadOS reports a desktop user agent, so a touch-capable "Mac" is an iPad.
	const iPad = /Macintosh/.test(agent) && navigator.maxTouchPoints > 1;
	if ((/iP(hone|ad|od)/.test(agent) || iPad) && /Safari/.test(agent) && !/CriOS|FxiOS/.test(agent)) {
		return 'ios-safari';
	}
	if (/Firefox\//.test(agent)) return 'firefox';
	return 'other';
}

export function installCapability(): InstallCapability {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return { kind: 'unsupported', detail: 'there is no browser here' };
	}
	if (runningStandalone() || installReported.value) {
		return {
			kind: 'installed',
			detail: 'Scout is already installed on this device, and this window is it.',
		};
	}
	if (deferred !== null) return { kind: 'promptable' };

	// NO DEFERRED PROMPT: either the browser is still deciding, or it will never
	// offer one. Only the second is worth instructions for, and the honest
	// distinction is whether a service worker is present at all — a browser
	// with no service worker cannot install a PWA, and telling its user to look
	// in a menu would be sending them to look for something that is not there.
	const hasServiceWorker = 'serviceWorker' in navigator;
	if (!hasServiceWorker) {
		return {
			kind: 'unsupported',
			detail: 'this browser cannot install web applications, so Scout is used in the tab.',
		};
	}
	switch (instructionBrowser()) {
		case 'ios-safari':
			return {
				kind: 'manual',
				detail:
					'On iPhone and iPad, installation is Safari’s own: tap the Share button, then “Add to Home Screen”.',
			};
		case 'firefox':
			return {
				kind: 'manual',
				detail:
					'Firefox does not offer a one-tap install. Its menu has “Install” for this site on Android; on desktop, use Chrome or Edge.',
			};
		default:
			return {
				kind: 'manual',
				detail:
					'This browser has not offered a one-tap install. Chrome and Edge offer it from the address bar’s install icon, or the ⋮ menu’s “Install app”.',
			};
	}
}

/** The revision a view depends on, so a control re-renders when the browser's
 *  answer changes. Reading `installCapability()` is what gives the state; this
 *  is what makes the read reactive. */
export function installRevision(): number {
	return promptRevision.value;
}

export type InstallOutcome =
	/** The Owner accepted and the browser says it is installing. */
	| { kind: 'accepted' }
	/** The Owner dismissed the browser's own prompt. Reported as dismissal. */
	| { kind: 'dismissed' }
	/** There was nothing to prompt with — the event was already consumed. */
	| { kind: 'unavailable' };

/**
 * RAISE THE BROWSER'S OWN INSTALL PROMPT — ONCE.
 *
 * `deferred` is cleared BEFORE `prompt()` is awaited, because the event is
 * single-use: calling `prompt()` twice on the same event rejects, and leaving
 * it in place would let a second click look like a working button that does
 * nothing. Clearing it first means a second click finds `unavailable`, which is
 * the truth.
 *
 * THE OUTCOME COMES FROM THE BROWSER AND IS PASSED THROUGH UNCHANGED. Nothing
 * here reports success on the strength of the prompt having been shown.
 */
export async function requestInstall(): Promise<InstallOutcome> {
	const event = deferred;
	if (event === null) return { kind: 'unavailable' };
	deferred = null;
	promptRevision.value += 1;
	try {
		await event.prompt();
		const choice = await event.userChoice;
		if (choice.outcome === 'accepted') {
			installReported.value = true;
			promptRevision.value += 1;
			return { kind: 'accepted' };
		}
		return { kind: 'dismissed' };
	} catch (error) {
		// A prompt that threw is a browser that changed its mind about the
		// event. Said as `unavailable` rather than as a dismissal, because the
		// Owner was never asked.
		void error;
		return { kind: 'unavailable' };
	}
}

/** Forget a deferred prompt — used by specimens, and by nothing in the app. */
export function resetInstallStateForTest(): void {
	deferred = null;
	installReported.value = false;
	promptRevision.value += 1;
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
	// `preventDefault()` IS WHAT MAKES THE DEFERRED PROMPT POSSIBLE. Chromium
	// shows its own mini-infobar unless the page cancels the event; the whole
	// point of capturing it is to raise the prompt when the OWNER asks, from
	// the control the Owner was looking for.
	window.addEventListener('beforeinstallprompt', (event: Event) => {
		event.preventDefault();
		deferred = event as InstallPromptEvent;
		promptRevision.value += 1;
	});
	window.addEventListener('appinstalled', () => {
		deferred = null;
		installReported.value = true;
		promptRevision.value += 1;
	});
}
